import * as React from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { Button, Divider, InputRef, Space, Table } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/lib/table'
import { useNavigate } from 'react-router-dom'
import { path } from '../../../../libs'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { useUrlFilters } from '../../../../libs/useUrlFilters'
import { useRolesList } from './hooks'
import { Role } from './types'

const FILTER_CONFIG = {
  id: { type: 'string' as const },
  description: { type: 'string' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '10', pagination: true },
  sortField: { type: 'string' as const, default: 'id', pagination: true },
  sortOrder: {
    type: 'string' as const,
    default: 'ascend',
    pagination: true,
  },
}

const SEARCH_KEYS = ['id', 'description'] as const

export const RoleList: React.FC = () => {
  const navigate = useNavigate()

  const { roles, loading, onChangeRolesList, totalCountOfRoles } =
    useRolesList()

  const { filters, setFilters } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 10
  const orderBy = filters.sortField || 'id'
  const orderDirection = filters.sortOrder === 'descend' ? '-' : ''

  const searchText = useMemo(() => {
    const result: Record<string, string> = {}
    for (const key of SEARCH_KEYS) {
      const value = filters[key]
      if (value) result[key] = value
    }
    return result
  }, [filters])

  const searchInput = useRef<InputRef>(null)

  const setSearchText: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  > = (updater) => {
    const next =
      typeof updater === 'function' ? updater({ ...searchText }) : updater
    const updates: Record<string, any> = { page: undefined }
    for (const key of SEARCH_KEYS) {
      updates[key] = next[key] || undefined
    }
    setFilters(updates as any)
  }

  useEffect(() => {
    onChangeRolesList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText])

  const columns: ColumnsType<Role> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps<Role>(
        'id',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ...getColumnSearchProps<Role>(
        'description',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: Role) => (
        <Button
          icon={<EyeOutlined />}
          title="View"
          onClick={() => navigate(path.roles.details(item.id))}
        />
      ),
    },
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Divider style={{ margin: 0 }} />
      <Table
        dataSource={roles}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="id"
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfRoles,
        }}
        onChange={(pagination, _tableFilters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current) {
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          }
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize =
              pagination.pageSize === 10
                ? undefined
                : String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          const field = sorter.field || sorter.columnKey
          // @ts-ignore
          const order = sorter.order as 'ascend' | 'descend' | undefined
          if (field) {
            updates.sortField = field === 'id' ? undefined : String(field)
            updates.sortOrder =
              order === 'descend'
                ? 'descend'
                : order === 'ascend'
                ? undefined
                : undefined
          }
          setFilters(updates as any)
        }}
      />
    </Space>
  )
}
